import { ViewIds } from '@ardoq/api-types';
import { Team } from '@ardoq/profiling';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { usePerformanceTracking } from 'tabview/graphComponent/usePerformanceTracking';
import {
  BubbleChartRenderingData,
  BubbleChartViewProperties,
  BubbleChartView,
  BubbleChartViewSettings,
  updateSelectedQuadrantViewSettings,
} from '@ardoq/bubble-chart';
import { isEqual } from 'lodash';
import { WorkspaceModeBubbleChartSourceDataArgs } from './sourceData/buildWorkspaceModeViewModelSourceData';
import { context$ } from 'streams/context/context$';
import { components$ } from 'streams/components/components$';
import { graphModel$ } from 'modelInterface/graphModel$';
import { scopeDiff$ } from 'scope/scopeDiff$';
import { activeDiffMode$ } from 'scope/activeDiffMode$';
import { activeFilter$ } from 'streams/filters/activeFilter$';
import { currentUserPermissionContext$ } from 'streams/currentUserPermissions/currentUserPermissionContext$';
import { locale$ } from 'streams/locale$';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { fields$ } from 'streams/fields/fields$';
import { DEFAULT_DEBOUNCE_TIME } from 'modelInterface/consts';
import {
  WorkspaceModeBubbleChartSourceDataLayer,
  WorkspaceModeBubbleChartSourceDataLayerProps,
} from './WorkspaceModeBubbleChartSourceDataLayer';
import { BubbleChartViewLegend } from './BubbleChartViewLegend';
import { DoqEmptyState } from './emptyState/DoqEmptyState';
import { updateSettingsBarData } from '@ardoq/view-settings';
import { BubbleChartZoomControls } from './zoomControls/BubbleChartZoomControls';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
} from 'rxjs';
import { shallowEqual } from 'utils/objectUtils';
import { ComponentType, useRef } from 'react';
import { WorkspaceModeInstanceViewSettings } from 'views/environmentals/viewSettings/types';
import {
  toWorkspaceModeSettingsControlsDependencies,
  toWorkspaceModeViewContext,
  type WorkspaceModeBubbleChartSettingsControlsDependencies,
} from './workspaceMode/settingsControls';
import {
  getViewSettingsForModelSourceData$,
  getViewSettingsForRenderingData$,
} from './streams/viewSettings$';
import { startBubbleChartTracking } from './streams/performanceTrackingUtils';
import { workspaceModeBubbleChartCommands } from './commands';

const VIEW_ID = ViewIds.BUBBLE;

const PerformanceTrackedBubbleChart = (props: BubbleChartViewProperties) => {
  usePerformanceTracking('bubble chart view render', 100, {
    viewId: VIEW_ID,
    team: Team.INSIGHT,
    metadata: { itemCount: props.data.length, isViewpointMode: false },
  });

  return <BubbleChartView {...props} />;
};

const publishSettingsControlsData = (
  dependencies: WorkspaceModeBubbleChartSettingsControlsDependencies
) => {
  const { viewInstanceId } = dependencies;
  if (!viewInstanceId) {
    return;
  }
  dispatchAction(
    updateSettingsBarData({
      viewId: VIEW_ID,
      settingsControlsDependencies: {
        ...dependencies,
        viewContext: toWorkspaceModeViewContext(dependencies),
      },
    }),
    viewInstanceId
  );
};

const getSettingsControlsData = (
  previousState: BubbleChartRenderingData,
  state: BubbleChartRenderingData
): WorkspaceModeBubbleChartSettingsControlsDependencies | null => {
  const previous = toWorkspaceModeSettingsControlsDependencies(previousState);
  const current = toWorkspaceModeSettingsControlsDependencies(state);

  return isEqual(previous, current) ? null : current;
};

const workspaceModeSettingsControlsHandlers = {
  getSettingsControlsData,
  publishSettingsControlsData,
};

type WorkspaceModeBubbleChartProps = {
  viewInstanceId: string;
  viewSettingsWithChanges$: Observable<
    WorkspaceModeInstanceViewSettings<ViewIds.BUBBLE>
  >;
};

const toSourceDataLayerProps = ([
  sourceDataArgs,
  viewSettingsForRenderingData,
]: [WorkspaceModeBubbleChartSourceDataArgs, BubbleChartViewSettings]) => ({
  ...sourceDataArgs,
  viewSettingsForRenderingData,
});

const startTracking = (
  sourceDataArgs: WorkspaceModeBubbleChartSourceDataArgs
): WorkspaceModeBubbleChartSourceDataArgs => ({
  ...sourceDataArgs,
  trackingId: startBubbleChartTracking(),
});

const getSourceDataArgs$ = (
  viewSettingsForModelSourceData$: Observable<BubbleChartViewSettings>
): Observable<WorkspaceModeBubbleChartSourceDataArgs> =>
  combineLatest({
    context: context$,
    components: components$,
    viewSettings: viewSettingsForModelSourceData$,
    graph: graphModel$,
    scopeDiffState: scopeDiff$,
    activeDiffMode: activeDiffMode$,
    activeFilterState: activeFilter$,
    permissionContext: currentUserPermissionContext$,
    locale: locale$,
    i18nLocale: i18nLocale$,
    fields: fields$,
  }).pipe(
    debounceTime(DEFAULT_DEBOUNCE_TIME),
    distinctUntilChanged(shallowEqual),
    map(startTracking)
  );

const getSourceDataLayerProps$ = ({
  viewSettingsWithChanges$,
}: WorkspaceModeBubbleChartProps) =>
  combineLatest([
    getSourceDataArgs$(
      getViewSettingsForModelSourceData$(viewSettingsWithChanges$)
    ),
    getViewSettingsForRenderingData$(viewSettingsWithChanges$),
  ]).pipe(map(toSourceDataLayerProps));

type ConnectedSourceDataLayerProps = Omit<
  WorkspaceModeBubbleChartSourceDataLayerProps,
  keyof WorkspaceModeBubbleChartSourceDataArgs | 'viewSettingsForRenderingData'
>;

const WorkspaceModeBubbleChart = (props: WorkspaceModeBubbleChartProps) => {
  const sourceDataLayerProps$Ref = useRef<ReturnType<
    typeof getSourceDataLayerProps$
  > | null>(null);
  if (!sourceDataLayerProps$Ref.current) {
    sourceDataLayerProps$Ref.current = getSourceDataLayerProps$(props);
  }

  const connectedComponentRef =
    useRef<ComponentType<ConnectedSourceDataLayerProps> | null>(null);
  if (!connectedComponentRef.current) {
    connectedComponentRef.current = connect(
      WorkspaceModeBubbleChartSourceDataLayer,
      sourceDataLayerProps$Ref.current
    );
  }
  const ConnectedSourceDataLayer = connectedComponentRef.current;

  return (
    <ConnectedSourceDataLayer
      viewInstanceId={props.viewInstanceId}
      bubbleChartCommands={workspaceModeBubbleChartCommands}
      settingsControlsHandlers={workspaceModeSettingsControlsHandlers}
      onRenderingDataChange={updateSelectedQuadrantViewSettings}
      BubbleChartComponent={PerformanceTrackedBubbleChart}
      ViewLegendComponent={BubbleChartViewLegend}
      EmptyStateComponent={DoqEmptyState}
      ZoomControlsComponent={BubbleChartZoomControls}
    />
  );
};

export { WorkspaceModeBubbleChart };
